// @flow

import React from 'react';
import type { Node } from 'react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import {
  Heading,
  ExpandableOverflow,
  Link,
  Markdown,
  MediaQuery,
  Banner as CommonBanner,
} from '@riseart/common';
import { Row, Col } from '@riseart/antd-provider';
import { ArtistEditorial, ArtistEditorialItem } from '@riseart/artist';
import { useLocale } from 'shared_services/redux/hooks/useLocale';
import { StretchToRight } from 'shared_hocs/common/StretchToRight';
import { Picture } from 'shared_components/common/artdirection/picture/Picture';
import { ArtistBreadcrumbs } from 'shared_components/artist/breadcrumbs/Breadcrumbs';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { WithMaxWordLength } from 'shared_hocs/common/WithMaxWordLength';
import { FollowButton } from 'shared_components/common/follow/Button';
import { FilterFollowsProvider } from 'shared_data/providers/follow/Follow';
import { ArtistShare } from 'shared_components/artist/share/Share';
import { CreateFollowProvider } from 'shared_data/providers/follow/CreateFollow';
import { ArtistCareerStage } from 'shared_components/artist/CareerStage';

import {
  artistBioCls,
  artistBioLeftCls,
  artistBioRightCls,
  artistBioAsideCls,
  artistBioNameCls,
  artistBioNameLongCls,
  artistBioNameVeryLongCls,
  artistBioNameExtraLongCls,
  artistBioContentCls,
  artistBioContentMainCls,
  artistBioDescriptionCls,
  artistBannerMainCls,
} from 'shared_components/artist/bio/Bio.less';

import { raScreenMdMax, raScreenLg } from '@riseart/antd-provider/dist/website/variables.less';

type Props = {
  data: Object,
  image?: Object,
  editorials: Array<Object>,
  showCv: boolean,
  children: Node,
  url: string,
};

/**
 * ArtistBio
 *
 * @param {Props} props
 */
export const ArtistBio = ({
  data,
  editorials,
  image = null,
  showCv,
  url,
  children,
}: Props): Node => {
  const { id, name, alias, mediums, styles, biography, careerStages } = data;
  const locale = useLocale();
  const artistCvUrl = showCv ? UrlAssembler.artistSubpage('artistCv', id, alias, { locale }) : null;

  return (
    <React.Fragment>
      <MediaQuery maxWidth={raScreenMdMax}>
        {(isTablet) =>
          isTablet || !image ? (
            <ArtistBreadcrumbs
              pageLabel="profile"
              id={id}
              name={name}
              alias={alias}
              mediums={mediums}
              styles={styles}
              disableArtistLink
            />
          ) : null
        }
      </MediaQuery>
      <div className={artistBioCls}>
        <div className={artistBioLeftCls}>
          <div className={artistBioAsideCls}>
            <MediaQuery minWidth={raScreenLg}>
              {(isLargeScreen) =>
                isLargeScreen ? (
                  <WithMaxWordLength text={name}>
                    {(maxWordLength) => (
                      <Heading
                        tag="h1"
                        level="1"
                        className={classNames(
                          artistBioNameCls,
                          maxWordLength >= 10 && maxWordLength < 13 && artistBioNameLongCls,
                          maxWordLength >= 13 && maxWordLength <= 15 && artistBioNameVeryLongCls,
                          maxWordLength > 15 && artistBioNameExtraLongCls,
                        )}
                      >
                        {name}
                      </Heading>
                    )}
                  </WithMaxWordLength>
                ) : (
                  <Heading tag="h1" level="1" className={artistBioNameCls}>
                    {name}
                  </Heading>
                )
              }
            </MediaQuery>
            {children}
            <FilterFollowsProvider>
              <CreateFollowProvider leadersList={data} />
              <FollowButton leaderId={id} leaderData={data} />
            </FilterFollowsProvider>

            <ArtistShare name={name} image={image} url={url} />

            <MediaQuery minWidth={raScreenLg}>
              {careerStages
                ? careerStages.map((stage) => <ArtistCareerStage stage={stage} />)
                : null}
            </MediaQuery>
          </div>
        </div>
        <div className={artistBioRightCls}>
          {image ? (
            <MediaQuery minWidth={raScreenLg}>
              {(isDesktop) => {
                const banner = (
                  <CommonBanner
                    className={artistBannerMainCls}
                    size="large"
                    media={
                      <Picture
                        type="artist.profileMainBanner"
                        artDirectionKey="artist.banner"
                        image={image}
                        alt={name}
                        lazyload={null}
                      />
                    }
                  />
                );
                return isDesktop ? <StretchToRight>{banner}</StretchToRight> : banner;
              }}
            </MediaQuery>
          ) : null}
          <MediaQuery minWidth={raScreenLg}>
            {(isDesktop) =>
              isDesktop && image ? (
                <ArtistBreadcrumbs
                  pageLabel="profile"
                  id={id}
                  name={name}
                  alias={alias}
                  mediums={mediums}
                  styles={styles}
                  disableArtistLink
                />
              ) : null
            }
          </MediaQuery>
          <Row
            type="flex"
            gutter={48}
            className={classNames(artistBioContentCls, !image && artistBioContentMainCls)}
          >
            {editorials && editorials.length ? (
              <Col xs={0} sm={6}>
                <ArtistEditorial>
                  {editorials.map(({ id, text }) => (
                    <ArtistEditorialItem key={id}>{text}</ArtistEditorialItem>
                  ))}
                </ArtistEditorial>
              </Col>
            ) : null}
            <Col xs={24} sm={18}>
              <ExpandableOverflow
                maxHeight={350}
                showMore={<FormattedMessage id="common.read_more" />}
                showLess={<FormattedMessage id="common.read_less" />}
                footerChildren={
                  artistCvUrl ? (
                    <FormattedMessage id="components.artist.viewCv">
                      {(text: string) => (
                        <Link to={artistCvUrl} title={text}>
                          {text}
                        </Link>
                      )}
                    </FormattedMessage>
                  ) : null
                }
              >
                <Markdown
                  key={id}
                  className={artistBioDescriptionCls}
                  text={biography}
                  components={{
                    // Overwrite H2 to <Heading tag="h3" level="3">
                    h2: ({ node, level, children, ...rest }) => (
                      <Heading {...rest} tag="h3" level="3">
                        {children}
                      </Heading>
                    ),
                    // Overwrite H3 to <Heading tag="h4" level="4">
                    h3: ({ node, level, children, ...rest }) => (
                      <Heading {...rest} tag="h4" level="4">
                        {children}
                      </Heading>
                    ),
                  }}
                />
              </ExpandableOverflow>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};
